import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Link,
  Alert,
  useTheme,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import {
  PictureAsPdf,
  Forum as ForumIcon,
  NoteAlt as NoteAltIcon,
  AddRounded,
  HelpRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useChartMindContext } from "context/ChartMindSession";
import { useAuth } from "hooks/useAuth";
import ConfirmDialog from "components/common/ConfirmDialog";
import ChartMindHelpDialog from "components/dashboard/chartmind/ChartMindHelpDialog";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import CustomMessageInput from "components/chat_new/CustomMessageInput";
import PdfViewerDialog from "components/common/PdfViewerDialog";
import {
  mergeSourcesByUrl,
  displayLabel,
  stripSourceCitations,
} from "utils/clinicalSources";

const PROMPT_ID = "chartmind-peerview-consultation";

// ─── source / citation helpers (same pattern as AskMeAnythingPage) ──────────

const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.4; }
  50% { transform: scale(1.05); opacity: 0.7; }
  100% { transform: scale(0.95); opacity: 0.4; }
`;

function SourceLinks({ sources, onOpenPdf }) {
  if (!sources?.length) return null;
  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.15)" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        Source documents
      </Typography>
      {sources.map((s) => (
        <Box
          key={s.downloadURL}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
        >
          <PictureAsPdf
            sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }}
          />
          <Link
            component="button"
            variant="caption"
            onClick={() => onOpenPdf(s)}
            sx={{ textAlign: "left", wordBreak: "break-word" }}
          >
            {displayLabel(s)}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

// ─── context builder ─────────────────────────────────────────────────────────

function buildEncounterContext(session) {
  const parts = [];

  // Imported patient specifics, so the consultation knows who the encounter is
  // about. In a live session the full shared record is present; after a reload
  // only identity persists (see serializeSession), so this degrades gracefully.
  const pc = session.patientContext?.value;
  if (pc?.patientUserId) {
    const lines = [];
    if (pc.patientName) lines.push(`Name: ${pc.patientName}`);
    if (pc.patientDob) lines.push(`Date of birth: ${pc.patientDob}`);
    const rec = pc.record || {};
    if (rec.demographics?.sex) lines.push(`Sex: ${rec.demographics.sex}`);
    if (rec.demographics?.weight != null) lines.push(`Weight: ${rec.demographics.weight}`);
    if (rec.conditions?.length) lines.push(`Medical conditions: ${rec.conditions.join(", ")}`);
    if (rec.surgical?.length) lines.push(`Surgical history: ${rec.surgical.join(", ")}`);
    if (rec.allergies?.length) lines.push(`Allergies: ${rec.allergies.join(", ")}`);
    if (rec.medications?.length) {
      const meds = rec.medications
        .map((m) =>
          [m.name, [m.dosage, m.frequency].filter(Boolean).join(", ")]
            .filter(Boolean)
            .join(" — "),
        )
        .filter(Boolean)
        .join("; ");
      if (meds) lines.push(`Medications: ${meds}`);
    }
    if (rec.other && typeof rec.other === "object") {
      const otherStr = Object.entries(rec.other)
        .filter(([, v]) => v && String(v).trim())
        .map(([k, v]) => `${k}: ${v}`)
        .join("; ");
      if (otherStr) lines.push(`Other history: ${otherStr}`);
    }
    if (lines.length) parts.push(`PATIENT (imported record):\n${lines.join("\n")}`);
  }

  const transcript =
    session.recording?.debouncedTranscript || session.recording?.transcript;
  if (transcript?.trim()) {
    parts.push(`ENCOUNTER TRANSCRIPT:\n${transcript.trim()}`);
  }

  const ddx = session.diagnosis?.ddxData;
  if (ddx) {
    const primary = (ddx.primary_diagnoses || [])
      .map((d) => d.diagnosis || d.name || d)
      .filter(Boolean)
      .join(", ");
    const alt = (ddx.alternative_diagnoses || [])
      .map((d) => d.diagnosis || d.name || d)
      .filter(Boolean)
      .join(", ");
    if (primary) parts.push(`AI DIFFERENTIAL (PRIMARY): ${primary}`);
    if (alt) parts.push(`AI DIFFERENTIAL (ALTERNATIVE): ${alt}`);
  }

  const selected = session.diagnosis?.selectedDiagnoses;
  if (selected?.length) {
    parts.push(
      `SELECTED DIAGNOSES: ${selected.map((d) => d.diagnosis || d.name || d).filter(Boolean).join(", ")}`,
    );
  }

  const confirmed = session.confirmDiagnosis?.selectedConfirmedDiagnoses;
  if (confirmed?.length) {
    parts.push(
      `CONFIRMED DIAGNOSES: ${confirmed.map((d) => d.diagnosis || d.name || d).filter(Boolean).join(", ")}`,
    );
  }

  const treatmentPlan = session.treatment?.planData;
  if (treatmentPlan) {
    const str =
      typeof treatmentPlan === "string"
        ? treatmentPlan
        : JSON.stringify(treatmentPlan, null, 2);
    parts.push(`TREATMENT PLAN:\n${str}`);
  }

  const noteSections = session.chart?.noteSections;
  if (noteSections && Object.keys(noteSections).length > 0) {
    const chartStr = Object.entries(noteSections)
      .map(([k, v]) => `${k.toUpperCase()}:\n${v}`)
      .join("\n\n");
    parts.push(`CLINICAL NOTE:\n${chartStr}`);
  }

  return parts.join("\n\n");
}

function hasEncounterData(session) {
  return !!(
    session.recording?.transcript ||
    session.diagnosis?.ddxData ||
    session.diagnosis?.selectedDiagnoses?.length ||
    (session.chart?.noteSections &&
      Object.keys(session.chart.noteSections).length > 0)
  );
}

// ─── no-encounter state ───────────────────────────────────────────────────────

function NoEncounterBody() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        textAlign: "center",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <ForumIcon sx={{ fontSize: 64 }} color="primary" />
      </Box>
      <Typography
        variant="h4"
        sx={{ mb: 2, fontWeight: 600, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
      >
        PeerView Case Consultation
      </Typography>
      <Typography
        variant="body1"
        sx={{ mb: 4, color: "text.secondary", lineHeight: 1.6, maxWidth: 480 }}
      >
        Ask questions about your ChartMind encounter. Start recording an encounter to get started.
      </Typography>
      <Button
        variant="contained"
        startIcon={<NoteAltIcon />}
        onClick={() => navigate("/dashboard/chartmind")}
        sx={{ textTransform: "none", fontWeight: 500 }}
      >
        Start a ChartMind Encounter
      </Button>
    </Box>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

const PeerViewPage = () => {
  const navigate = useNavigate();
  const session = useChartMindContext();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const isOrgAdmin = userData?.role === "admin" && Boolean(organizationId);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const messages = session.peerViewMessages;
  const setMessages = session.setPeerViewMessages;

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    title: null,
  });

  const chatBoxRef = useRef(null);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleOpenPdf = (source) => {
    if (source?.downloadURL) {
      setPdfViewer({
        open: true,
        url: source.downloadURL,
        title: displayLabel(source),
      });
    }
  };

  const performNewEncounter = useCallback(() => {
    session.session.clearSession();
    session.clearPeerViewMessages();
    setError(null);
  }, [session]);

  const handleNewEncounterClick = useCallback(() => {
    if (hasEncounterData(session)) {
      setConfirmNewOpen(true);
      return;
    }
    performNewEncounter();
  }, [session, performNewEncounter]);

  const handleConfirmNewEncounter = useCallback(() => {
    performNewEncounter();
    setConfirmNewOpen(false);
  }, [performNewEncounter]);

  const handleSend = async (text) => {
    const trimmed = (typeof text === "string" ? text : inputValue).trim();
    if (!trimmed || isTyping) return;

    setInputValue("");
    setError(null);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: trimmed, sources: [] },
    ]);
    setIsTyping(true);

    // Re-read context on every send so it reflects latest session state
    const contextBlock = buildEncounterContext(session);
    const fullMessage = contextBlock
      ? `[ChartMind Encounter Context]\n${contextBlock}\n\n[QUESTION]\n${trimmed}`
      : trimmed;

    try {
      const [result, clinicalRefs] = await Promise.all([
        invokeLLMPrompt({
          promptId: PROMPT_ID,
          userMessage: fullMessage,
          includeEmbeddedDocuments: true,
          embeddedDocumentsOptions: { chunkLimit: 8 },
        }),
        retrieveClinicalReferences(trimmed, { promptId: PROMPT_ID }).catch(
          () => [],
        ),
      ]);

      const embeddedDocs = result.embeddedDocuments?.documents ?? [];
      const sources = mergeSourcesByUrl(embeddedDocs, clinicalRefs);
      const content = stripSourceCitations(result.output || "(No response)");

      setMessages((prev) => [...prev, { role: "assistant", content, sources }]);
    } catch (err) {
      const msg = err.message || "Request failed.";
      setError(msg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${msg}`,
          isError: true,
          sources: [],
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const noEncounter = !hasEncounterData(session);

  return (
    <Box
      sx={{
        height: { xs: "calc(100dvh - 65px)", sm: "100dvh" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top bar — desktop */}
      {!isXs && (
        <Box
          sx={{
            ...theme.mixins.toolbar,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pl: 3,
            pr: 2,
            flexShrink: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ForumIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            <Typography variant="subtitle2" color="text.secondary">
              PeerView
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Help">
              <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: "text.secondary" }}>
                <HelpRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Back to ChartMind">
              <IconButton
                onClick={() => navigate("/dashboard/chartmind")}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <NoteAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="New Encounter">
              <IconButton onClick={handleNewEncounterClick} size="small" sx={{ color: "text.secondary" }}>
                <AddRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Mobile action buttons */}
      {isXs && (
        <Box sx={{ position: "fixed", top: "12px", right: "20px", zIndex: 1150, display: "flex", gap: 0.5 }}>
          <Tooltip title="Help">
            <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: "text.secondary" }}>
              <HelpRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Back to ChartMind">
            <IconButton
              onClick={() => navigate("/dashboard/chartmind")}
              sx={{ color: "text.secondary" }}
              size="small"
            >
              <NoteAltIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="New Encounter">
            <IconButton onClick={handleNewEncounterClick} sx={{ color: "text.secondary" }} size="small">
              <AddRounded />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* No-encounter splash */}
      {noEncounter && <NoEncounterBody />}

      {/* Chat area + PDF dialog — only when there's an active encounter */}
      {!noEncounter && <><Box
        sx={{
          margin: "0 auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          maxWidth: "970px",
          px: 2,
          ...(messages.length === 0
            ? {
                justifyContent: "flex-start",
                alignItems: "center",
                paddingTop: { sm: "1rem", md: "20vh" },
              }
            : { justifyContent: "flex-start" }),
        }}
      >
        {/* Intro / empty state */}
        {messages.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 0,
              px: 2,
              maxWidth: "600px",
              width: "100%",
            }}
          >
            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
              <ForumIcon sx={{ fontSize: 64 }} color="primary" />
            </Box>
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: { xs: "1.75rem", sm: "2.125rem" },
              }}
            >
              PeerView Case Consultation
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 3, color: "text.secondary", lineHeight: 1.6 }}
            >
              Ask a question about your ChartMind encounter.</Typography>
          </Box>
        )}

        {/* Error alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ mb: 2, width: "100%", maxWidth: "600px" }}
          >
            {error}
          </Alert>
        )}

        {/* Messages */}
        {messages.length > 0 && (
          <Box
            ref={chatBoxRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              mb: 1,
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{ display: "flex", margin: "5px 0", width: "100%" }}
              >
                <Box
                  sx={{
                    backgroundColor:
                      msg.role === "user"
                        ? "primary.main"
                        : msg.isError
                          ? "#ff7979"
                          : "#eee",
                    color:
                      msg.role === "user" ? "primary.contrastText" : "text.primary",
                    borderRadius:
                      msg.role === "user"
                        ? "8px 0px 8px 8px"
                        : "0px 8px 8px 8px",
                    padding: "10px",
                    maxWidth: "80%",
                    width: "fit-content",
                    marginLeft: msg.role === "user" ? "auto" : "0",
                    marginRight: msg.role === "user" ? "0" : "auto",
                    boxShadow: 1,
                    "& p": { margin: 0 },
                    "& p + p": { marginTop: "16px" },
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{ color: "inherit", wordBreak: "break-word" }}
                  >
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Typography>
                  {msg.role === "assistant" && msg.sources?.length > 0 && (
                    <SourceLinks sources={msg.sources} onOpenPdf={handleOpenPdf} />
                  )}
                </Box>
              </Box>
            ))}

            {isTyping && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  pl: 2,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    animation: `${pulse} 1.2s ease-in-out infinite`,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Thinking…
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            ...(messages.length === 0
              ? { position: "relative", maxWidth: "600px" }
              : { marginBottom: "6px" }),
          }}
        >
          <CustomMessageInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            handleSendRequest={handleSend}
            userData={null}
            hasMessages={messages.length > 0}
          />
        </Box>
      </Box>

      {/* PDF Viewer */}
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        title={pdfViewer.title}
        onClose={() => setPdfViewer({ open: false, url: null, title: null })}
      /></>}

      {/* Simulation warning for admins */}
      {(isGlobalAdmin || isOrgAdmin) && (
        <Alert
          severity="warning"
          variant="standard"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/dashboard/prompts")}
            >
              Manage Prompts
            </Button>
          }
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            fontSize: "0.75rem",
            py: 0.25,
            alignItems: "center",
            "& .MuiAlert-icon": { fontSize: "1rem" },
          }}
        >
          Simulation mode for prompt development. Not intended for medical use.
        </Alert>
      )}

      <ChartMindHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <ConfirmDialog
        open={confirmNewOpen}
        onClose={() => setConfirmNewOpen(false)}
        title="Start a new encounter?"
        message="Starting a new encounter will clear your current ChartMind session and this PeerView conversation."
        confirmLabel="Start new"
        confirmColor="primary"
        onConfirm={handleConfirmNewEncounter}
      />
    </Box>
  );
};

export default PeerViewPage;
